import React, { useEffect, useState } from "react";
import { Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import {
  IoClipboardOutline,
  IoCheckmarkCircleOutline,
  IoTimeOutline,
  IoCloseCircleOutline,
  IoCalendarSharp,
  IoChevronDownOutline,
} from "react-icons/io5";

import "./buyerDashboard.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend
);

const monthsLabels = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const yearOptions = [2028, 2027, 2026, 2025, 2024];

const iconMap = {
  "clipboard-outline": <IoClipboardOutline size={28} color="#fff" />,
  "checkmark-circle-outline": <IoCheckmarkCircleOutline size={28} color="#fff" />,
  "time-outline": <IoTimeOutline size={28} color="#fff" />,
  "close-circle-outline": <IoCloseCircleOutline size={28} color="#fff" />,
};

const BuyerDashboard = () => {
  const [stats, setStats] = useState([
    { title: "Total Bids Placed", count: 0, icon: "clipboard-outline", bg: "#062d59" },
    { title: "Accepted Bids", count: 0, icon: "checkmark-circle-outline", bg: "#274c87" },
    { title: "Expired Bids", count: 10, icon: "time-outline", bg: "#e3080c" },
    { title: "Rejected Bids", count: 4, icon: "close-circle-outline", bg: "#f14b4e" },
  ]);

  const [buyerId, setBuyerId] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [monthlyCounts, setMonthlyCounts] = useState(Array(12).fill(0));
  const [modalVisible, setModalVisible] = useState(false);
  const [spendingData, setSpendingData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(String(year));
  const [modalVisibleRev, setModalVisibleRev] = useState(false);

  useEffect(() => {
  const user = JSON.parse(sessionStorage.getItem('user'));
  const userId = user?.id;
     setBuyerId((userId));
  }, []);


  useEffect(() => {
    if (!buyerId) return;

    const fetchData = async () => {
      try {
        const totalRes = await fetch(`https://vyaparapi.emedha.in/api/buyer/bids/total?userId=${buyerId}`);
        const totalData = await totalRes.json();
        const acceptedRes = await fetch(`https://vyaparapi.emedha.in/api/buyer/bids/accepted/count?userId=${buyerId}`);
        const acceptedData = await acceptedRes.json();
        const rejectedExpiredRes = await fetch(`https://vyaparapi.emedha.in/api/buyer/bids/rejected-expired/count?userId=${buyerId}`);
        const rejectedExpiredData = await rejectedExpiredRes.json();

        setStats((prev) =>
          prev.map((stat) => {
            if (stat.title === "Total Bids Placed") return { ...stat, count: Number(totalData.total) };
            if (stat.title === "Accepted Bids") return { ...stat, count: Number(acceptedData.count) };
            if (stat.title === "Rejected Bids") return { ...stat, count: Number(rejectedExpiredData.rejected || 0) };
            if (stat.title === "Expired Bids") return { ...stat, count: Number(rejectedExpiredData.expired || 0) };
            return stat;
          })
        );
      } catch (err) {
        console.error("Error fetching stats data:", err);
      }
    };

    fetchData();
  }, [buyerId]);

  useEffect(() => {
    if (!buyerId) return;

    const fetchMonthlyCounts = async () => {
      try {
        const response = await fetch(
          `https://vyaparapi.emedha.in/api/buyer/bids/monthly-count?buyerId=${buyerId}&year=${year}`
        );
        const data = await response.json();
        setMonthlyCounts(data.monthlyCounts || Array(12).fill(0));
      } catch (err) {
        console.error("Error fetching monthly counts:", err);
      }
    };

    fetchMonthlyCounts();
  }, [buyerId, year]);

  useEffect(() => {
    const fetchSpendingData = async () => {
      try {
        const response = await fetch(
          `https://vyaparapi.emedha.in/api/buyer/bids/spending-breakdown?userId=${buyerId}&year=${year}`
        );
        const data = await response.json();
        setSpendingData(data || []);
      } catch (error) {
        console.error("Error fetching spending data:", error);
      }
    };

    if (buyerId && year) {
      fetchSpendingData();
    }
  }, [buyerId, year]);

  const lineChartData = {
    labels: monthsLabels,
    datasets: [
      {
        label: "Bids",
        data: monthlyCounts,
        fill: false,
        backgroundColor: "#e3080c",
        borderColor: "#e3080c",
        tension: 0.1,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };

  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: { labels: { color: "#062d59" } },
    },
    scales: {
      x: {
        ticks: { color: "#062d59" },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { color: "#062d59" },
        grid: { color: "#eee" },
      },
    },
  };

  const filteredSpendingData = spendingData.filter(
    (item) => typeof item.revenue === "number" && !isNaN(item.revenue)
  );

  const pieChartData = {
    labels: filteredSpendingData.map((item) => item.name || "Unknown"),
    datasets: [
      {
        data: filteredSpendingData.map((item) => Number(item.revenue)),
        backgroundColor: filteredSpendingData.map((item) => item.color || "#ccc"),
        hoverOffset: 30,
      },
    ],
  };

  const pieChartOptions = {
    plugins: {
      legend: {
        position: "right",
        labels: { color: "#333", font: { size: 14 } },
      },
    },
  };

  return (
    <div className="dashboard-container">
      {/* Statistic Cards */}
      <div className="stats-cards">
        {stats.map((stat, idx) => (
          <div
            key={idx}
            className="stat-card"
            style={{ backgroundColor: stat.bg }}
          >
            {iconMap[stat.icon]}
            <p className="stat-title">{stat.title}</p>
            <p className="stat-count">{stat.count}</p>
          </div>
        ))}
      </div>

      {/* Monthly Bids Chart */}
      {/* <div className="chart-container">
        <div className="chart-header">
          <h3 className="chart-title">Bids in {year}</h3>
          <button
            className="year-button"
            onClick={() => setModalVisible(true)}
          >
            Year: {year}
          </button>
        </div>

        <Line data={lineChartData} options={lineChartOptions} />

        {modalVisible && (
          <div
            className="modal-overlay"
            onClick={() => setModalVisible(false)}
          >
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="modal-title">Select Year</h3>
              {yearOptions.map((yr) => (
                <div
                  key={yr}
                  onClick={() => {
                    setYear(yr);
                    setModalVisible(false);
                  }}
                  className={`modal-option ${yr === year ? "selected" : ""}`}
                >
                  {yr}
                </div>
              ))}
              <button
                className="modal-close-button"
                onClick={() => setModalVisible(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>


      <div className="spending-container">
        <h3 className="spending-title">Spending Breakdown ({year})</h3>

        <button
          onClick={() => setModalVisibleRev(true)}
          className="spending-year-button"
        >
          <IoCalendarSharp size={18} style={{ marginRight: 8 }} />
          {selectedYear}
          <IoChevronDownOutline size={18} style={{ marginLeft: 8 }} />
        </button>

        {modalVisibleRev && (
          <div
            className="modal-overlay"
            onClick={() => setModalVisibleRev(false)}
          >
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="modal-title">Select Year</h3>
              {yearOptions.map((yr) => (
                <div
                  key={yr}
                  onClick={() => {
                    setSelectedYear(String(yr));
                    setYear(yr);
                    setModalVisibleRev(false);
                  }}
                  className={`modal-option ${String(yr) === selectedYear ? "selectedAlt" : ""}`}
                >
                  {yr}
                </div>
              ))}
              <button
                className="modal-close-button"
                onClick={() => setModalVisibleRev(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        <Pie data={pieChartData} options={pieChartOptions} />
      </div> */}
      <div className="charts-wrapper">
  {/* Line Chart Section */}
  <div className="line-chart">
    <h3 className="chart-title">Bids in {year}</h3>
    <Line data={lineChartData} options={lineChartOptions} />
  </div>

  {/* Pie Chart Section */}
  <div className="pie-chart">
    <h3 className="chart-title">Spending Breakdown ({year})</h3>
    <Pie data={pieChartData} options={pieChartOptions} />
  </div>
</div>

    </div>
  );
};

export default BuyerDashboard;
